import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable } from 'mobx';
import { AppUI, LoadingState } from '@seedlang/state';
import styled from '@emotion/styled';
import MembershipOptionMemrev from 'components/membership/membership_option_memrev';
import { isPresent, sleep } from '@seedlang/utils';
import Cookies from 'js-cookie';
import Alert from 'components/alert';
import { Theme } from '@seedlang/constants';
import { MembershipTypeStore, PaymentMigrationStore } from '@seedlang/stores';
import Link from 'components/link';
import Text from 'components/text';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import Button from 'components/button/button';
import Stack, { gaps } from 'components/layout/stack';
import SegmentedControl from 'components/segmented_control';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media only screen and (max-width : 760px) {
    width: 100%;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  margin-top: 30px;
`;

const OptionsGridWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const MembershipOptionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding-bottom: 10px;
  width: 100%;
  max-width: 1080px;
  justify-content: center;
  
  @media only screen and (max-width: 730px) {
    padding: 0 20px;
  }
  
  @media only screen and (min-width: 731px) and (max-width: 1115px) {
    max-width: 800px;
  }
`;

const MembershipChangeNotice = styled.div`
  background: white;
  padding: 20px;
  width: 100%;
  margin-bottom: 20px;
  line-height: 1.5;
  transition: background 0.3s ease;

  &.flash {
    animation: flashAttention 0.75s ease;
  }

  @keyframes flashAttention {
    0%, 100% { background: white; }
    50% { background: ${() => `${AppUI.site.secondaryAccentColor }40`}; }
  }
`;

const Footnote = styled.div`
  font-size: 12px;
  color: ${props => props.color};
  line-height: 15px;
  margin: 5px auto;
  text-align: center;
  overflow-wrap: break-word;
  max-width: 1080px;
  
  @media only screen and (max-width: 730px) {
    padding: 0 20px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FreeAccount = styled.div`
  display: flex;
  flex-direction: column;  
  align-items: center;
  background: #FFF;
  margin-top: 15px;
  width: 100%;
  max-width: 1080px;
  padding: 20px;
  border-radius: 5px;
  
  @media only screen and (max-width: 730px) {
    margin: 15px 20px 0;
    width: calc(100% - 40px);
  }
`;

const Title = styled.div`
  display: block;
  direction: auto;
  font-size: 22px;
  line-height: 22px;
  font-weight: bold;
  text-align: center;
  color: black;
  margin: 0;
  `;

const SegmentedControlContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;
`;

const CurrencyDisabledDisclaimer = styled.div`
  font-size: 12px;
  line-height: 16px;
  max-width: 600px;
  text-align: center;
  color: ${Theme.gray};
  padding: 0 ${gaps.md};

  a { color: inherit; }
`;

const MembershipFooter = styled.div`
  background: #FFF;
  margin-top: 15px;
  width: 100%;
  max-width: 1080px;
  padding: 10px 20px;
  border-radius: 5px;
  
  @media only screen and (max-width: 730px) {
    margin: 15px 20px 0;
    width: calc(100% - 40px);
  }
`;

@observer
class MembershipOptionsMemrev extends React.Component {
  @observable loadedMembershipGroup = false;
  @observable membershipsFilter = 1;
  @observable subscriptionLoaded = false;
  @observable waitlistedMembershipTypeIds = [];
  membershipChangeNoticeRef = React.createRef();
  waitlistMutationVersion = 0;
  waitlistStatusesRequested = false;

  constructor(props) {
    super(props);
    if (!AppUI.userIsLoaded) {
      AppUI.loadUser();
    }
    AppUI.scrollToPageTop();
    this.disposer = autorun(() => {
      if (AppUI.userIsLoaded && AppUI.user.currentUserGroup?.membershipGroupId && !this.loadedMembershipGroup) {
        this.loadedMembershipGroup = true;
        this.loadMembershipGroup();
      }
      if (AppUI.userIsLoaded && AppUI.userIsSignedIn && !this.waitlistStatusesRequested) {
        this.waitlistStatusesRequested = true;
        this.loadWaitlistStatuses();
      }
      if (AppUI.userIsLoaded && AppUI.userSubscriptionStore.hasIndexData && !this.subscriptionLoaded) {
        this.subscriptionLoaded = true;
        if (this.props.activeSubscription?.membership?.numberMonths === 12) {
          this.onYearly();
        }
      }
    });
  }
  componentWillUnmount() {
    this.disposer && this.disposer();
  }
  onMonthly = () => {
    this.membershipsFilter = 1;
  }
  onYearly = () => {
    this.membershipsFilter = 12;
  }
  onToggle = () => {
    this.membershipsFilter = this.membershipsFilter === 1 ? 12 : 1;
  }
  handleMembershipChangePendingClick = async () => {
    const element = this.membershipChangeNoticeRef.current;
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('flash');
    await sleep(750);
    element.classList.remove('flash');
  }
  updateCurrency = (currency) => {
    if (this.currency === currency) return;
    if (AppUI.userIsSignedIn) {
      AppUI.user.set('currency', currency);
      AppUI.authUserStore.update({ data: { currency } });
    } else {
      AppUI.cookieStore.set('currency', currency);
    }
  }

  @computed get membershipOptionWidth() {
    if (AppUI.layout.viewportWidth <= 730 || this.filteredMemberships.length === 1) {
      return '100%';
    }
    if (AppUI.layout.viewportWidth <= 1115 || (this.filteredMemberships.length === 4)) {
      return 'calc(50% - 7.5px)';
    }
    return '350px';
  }

  @computed get isMultiRowLayout() {
    const numOptions = this.filteredMemberships.length;
    if (AppUI.layout.viewportWidth <= 730) {
      return numOptions > 1;
    } else if (AppUI.layout.viewportWidth <= 1115) {
      return numOptions > 2;
    } else {
      return numOptions > 3;
    }
  }

  @computed get siteId() {
    return Cookies.get('site_id');
  }

  @computed get currency() {
    if (AppUI.userIsSignedIn) {
      return AppUI.user?.currency?.toUpperCase() ?? 'USD';
    }
    console.log('cookieStore.currency', AppUI.cookieStore.currency);
    console.log('user.currency', AppUI.user?.currency);
    return AppUI.cookieStore.currency?.toUpperCase() ?? AppUI.user?.currency?.toUpperCase() ?? 'USD';
  }

  @computed get currencySymbol() {
    return this.currency?.toUpperCase() === 'USD' ? '$' : '€';
  }

  @computed get isCurrencyChangeDisabled() {
    return this.props.activeSubscription?.isActive || this.props.activeSubscription?.isTrialing;
  }

  @computed get otherCurrency() {
    return this.props.activeSubscription?.currency === 'USD' ? 'EUR' : 'USD';
  }

  @computed get hasNextPhaseMembership() {
    return isPresent(this.props.activeSubscription?.nextPhaseMembership?.id);
  }

  @computed get hasDowngradePending() {
    if (!this.hasNextPhaseMembership) return false;

    const nextPosition = this.props.activeSubscription.nextPhaseMembership.position;
    const currentPosition = this.props.activeSubscription.membership?.position;

    if (nextPosition == null || currentPosition == null) return false;

    return nextPosition < currentPosition;
  }

  @computed get hasSameTierFrequencyChangePending() {
    if (!this.hasNextPhaseMembership) return false;

    const nextMembership = this.props.activeSubscription.nextPhaseMembership;
    const currentMembership = this.props.activeSubscription.membership;

    if (!nextMembership.membershipTypeId || !currentMembership?.membershipTypeId) return false;

    const sameType = nextMembership.membershipTypeId === currentMembership.membershipTypeId;
    const differentFrequency = nextMembership.numberMonths !== currentMembership.numberMonths;

    return sameType && differentFrequency;
  }

  @computed get hasMembershipChangePending() {
    return this.hasDowngradePending || this.hasSameTierFrequencyChangePending;
  }
  loadMembershipGroup = () => {
    if (isPresent(AppUI.user.currentUserGroup)) {
      AppUI.membershipGroupStore.getShow({ ids: { membershipGroupId: AppUI.user.currentUserGroup.membershipGroupId } });
    }
  }
  loadWaitlistStatuses = async () => {
    const mutationVersion = this.waitlistMutationVersion;
    try {
      const response = await MembershipTypeStore.getWaitlistStatusesPromise();
      if (mutationVersion === this.waitlistMutationVersion) {
        this.waitlistedMembershipTypeIds = response.membershipTypeIds || [];
      }
    } catch {
      if (mutationVersion === this.waitlistMutationVersion) {
        this.waitlistedMembershipTypeIds = [];
      }
    }
  }
  joinWaitlist = async (membershipTypeId) => {
    if (!AppUI.userIsSignedIn) {
      AppUI.routeStore.routeToNamed('onboarding.register');
      return;
    }

    this.waitlistMutationVersion += 1;
    try {
      await MembershipTypeStore.joinWaitlistPromise(membershipTypeId);
    } catch (error) {
      this.loadWaitlistStatuses();
      throw error;
    }
    this.waitlistedMembershipTypeIds = [...new Set([
      ...this.waitlistedMembershipTypeIds,
      membershipTypeId,
    ])];
  }
  leaveWaitlist = async (membershipTypeId) => {
    this.waitlistMutationVersion += 1;
    try {
      await MembershipTypeStore.leaveWaitlistPromise(membershipTypeId);
    } catch (error) {
      this.loadWaitlistStatuses();
      throw error;
    }
    this.waitlistedMembershipTypeIds = this.waitlistedMembershipTypeIds.filter(id => id !== membershipTypeId);
  }

  @computed get filteredMemberships() {
    return isPresent(this.activeMemberships) ? this.activeMemberships.filter(item => this.membershipsFilter === item.numberMonths && item.active) : [];
  }

  @computed get activeMemberships() {
    const membershipGroup = AppUI.user?.currentUserGroup?.membershipGroupId ? AppUI.membershipGroupStore.showData : AppUI.user?.site?.membershipGroup;
    return isPresent(membershipGroup) ? membershipGroup.memberships.filter(item => item.active) : [];
  }
  onClick = async (membership, loadingState = null) => {
    if (AppUI.user.hasPermanentGroupMembership) {
      return;
    }
    AppUI.set('currentlyViewedMembership', membership);

    if (this.props.mode === 'payment_migration' || AppUI.isPaymentMigrationStartable) {
      loadingState = loadingState || new LoadingState();
      loadingState.started();
      try {
        await PaymentMigrationStore.updateMembershipPromise({
          membershipId: membership?.id,
          currency: this.currency,
        });
        loadingState.succeeded();
        AppUI.loadUser();
        AppUI.routeStore.routeToNamed('onboarding.migration');
      } catch {
        loadingState.failed('An error occurred while setting the membership.');
      }
      return;
    }
    const routeTo = window.location.href && window.location.href.match('settings') ? 'settings.memberships.create' : 'memberships.create';
    AppUI.routeStore.routeToNamed(routeTo, { membershipId: membership.id });
  }

  render() {
    return (
      <Wrapper>
        {
          AppUI.user.hasPermanentGroupMembership &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You have a free membership and will not be able to purchase a membership. Please contact us if you think there is an error.
          </Alert>
        }
        {
          AppUI.isPaymentMigrationStartable && this.props.mode !== 'payment_migration' &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You are currently subscribed via Patreon. If you wish to change your membership, you will then be asked to migrate your payment to our platform.
          </Alert>
        }
        {
          this.props.mode === 'payment_migration' && AppUI.isPaymentMigrationStartable &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You are currently migrating your payment method. Please select a membership level below. You will not be charged yet.
          </Alert>
        }
        <InnerWrapper>
          <Text heading={2} margin='0 auto 10px auto'>
            {this.props.activeSubscription?.membership ? 'My Membership' : 'Choose Your Membership'}
          </Text>
          <SegmentedControlContainer>
            <SegmentedControl
              options={[
                { value: 1, label: 'Monthly' },
                { value: 12, label: 'Yearly' },
              ]}
              activeOption={this.membershipsFilter}
              onChange={value => value === 1 ? this.onMonthly() : this.onYearly()}
            />
          </SegmentedControlContainer>
          <OptionsGridWrapper>
            <Column>
              <MembershipOptionWrapper>
                {
                  this.hasMembershipChangePending &&
                  <MembershipChangeNotice ref={this.membershipChangeNoticeRef}>
                    {
                      this.hasSameTierFrequencyChangePending ? (
                        <>
                          You are scheduled to switch to <strong>{this.props.activeSubscription.nextPhaseMembership?.numberMonths === 12 ? 'yearly' : 'monthly'} billing</strong> on {this.props.activeSubscription.currentPeriodEnd?.formattedDateWithFullYear}.
                          <br />
                          If you would like to change your membership, go to <Link className='underline' to='settings.billing'>Settings&nbsp;/&nbsp;Manage&nbsp;Membership</Link> to cancel this change first.
                        </>
                      ) : (
                        <>
                          You have a <strong>pending downgrade</strong> that will go into effect on {this.props.activeSubscription.currentPeriodEnd?.formattedDateWithFullYear}.
                          <br />
                          You have access to your {this.props.activeSubscription.membership?.title} benefits until then.
                          <br />
                          If you would like to change your membership, go to <Link className='underline' to='settings.billing'>Settings&nbsp;/&nbsp;Manage&nbsp;Membership</Link> to cancel your downgrade first.
                        </>
                      )
                    }
                  </MembershipChangeNotice>
                }
                {
                  this.filteredMemberships && this.filteredMemberships.map(item => {
                    return (
                      <MembershipOptionMemrev
                        key={item.id}
                        currency={this.currency}
                        currencySymbol={this.currencySymbol}
                        membership={item}
                        onClick={this.onClick}
                        onJoinWaitlist={this.joinWaitlist}
                        onLeaveWaitlist={this.leaveWaitlist}
                        isOnWaitlist={this.waitlistedMembershipTypeIds.includes(item.membershipType?.id)}
                        width={this.membershipOptionWidth}
                        mode={this.props.mode}
                        memberships={this.activeMemberships}
                        hasMembershipChangePending={this.hasMembershipChangePending}
                        onMembershipChangePendingClick={this.handleMembershipChangePendingClick}
                        toggleYearly={this.onToggle}
                        activeSubscription={this.props.activeSubscription}
                        paymentMigration={AppUI.isPaymentMigrationStartable || AppUI.isPaymentMigrationInProgress ? AppUI.user.paymentMigration : null}
                        expandable={this.isMultiRowLayout}
                      />
                    );
                  })
                }
              </MembershipOptionWrapper>
              {
                !AppUI.userIsSignedIn && !AppUI.membershipGroupStore.hasOutstandingRequests &&
                <FreeAccount>
                  <Title>Create Free Account</Title>
                  <p>
                    Undecided? It's free to register and try out our membership perks.
                  </p>
                  <Link to='onboarding.register'>
                    <Button>
                      Register
                    </Button>
                  </Link>
                </FreeAccount>
              }
              {
                isPresent(AppUI.site.membershipFooter) && !AppUI.membershipGroupStore.hasOutstandingRequests && !AppUI.user?.isMember &&
                  <MembershipFooter
                    dangerouslySetInnerHTML={{ __html: AppUI.site.membershipFooter }}
                  />
              }
            </Column>
          </OptionsGridWrapper>
          <Stack gap='sm' alignItems='center'>
            <Stack gap='xs' alignItems='center'>
              <SegmentedControl
                options={[
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                ]}
                activeOption={this.currency}
                onChange={value => this.updateCurrency(value)}
                small
                disabled={this.isCurrencyChangeDisabled}
              />

              {
                this.isCurrencyChangeDisabled &&
                <CurrencyDisabledDisclaimer>
                  Because you have a subscription in {this.props.activeSubscription?.currency}, you cannot currently change your currency.<br />
                  If you would absolutely like to pay in {this.otherCurrency}, please <Link className='underline' to='contact'>contact us</Link>.
                </CurrencyDisabledDisclaimer>
              }
            </Stack>

            <Footnote>
              Our membership platform is hosted by Seedlang, Inc. By signing up, you create a free user account with Seedlang (<a href='https://seedlang.com/terms' target='_blank' rel='noopener noreferrer'>Terms of Service</a>), which can also be used for any Easy Languages membership. If you choose a paid membership on this page, you'll enter into a paid subscription agreement with Easy Languages GmbH. You can also use an existing Easy Languages or Seedlang account.
            </Footnote>
          </Stack>
        </InnerWrapper>
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(MembershipOptionsMemrev);